import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect, forwardRef } from 'react';
import { Snackbar, Alert, IconButton, Slide } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ToastContext = createContext(null);

// Helper function to generate unique IDs
const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Define transition component outside to prevent re-renders
const SlideTransition = forwardRef(function SlideTransition(props, ref) {
  return (
    <Slide
      {...props}
      ref={ref}
      direction="left"   // comes from the right side
      style={{
        transformOrigin: 'top right', // ensures animation feels anchored at top-right
      }}
    />
  );
});

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timeoutsRef = useRef({});
  const toastsRef = useRef(toasts);

  // Mirror `toasts` into a ref so showToast can read the latest value without
  // depending on `toasts` itself - keeps showToast (and success/error/etc,
  // which derive from it) referentially stable across renders. Without this,
  // every toast recreates showToast/error with a new reference, which can
  // retrigger any useEffect/useCallback elsewhere that lists them as a dep
  // (e.g. a fetch-on-error handler calling ToastError in a loop).
  useEffect(() => {
    toastsRef.current = toasts;
  }, [toasts]);

  // Clean up timeout when component unmounts
  const clearTimeout = useCallback((id) => {
    if (timeoutsRef.current[id]) {
      clearTimeout(timeoutsRef.current[id]);
      delete timeoutsRef.current[id];
    }
  }, []);

  const handleClose = useCallback((id) => {
    // Clear any pending timeout
    clearTimeout(id);

    // Mark toast as closed (triggers exit animation)
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, open: false } : toast
      )
    );

    // Remove toast from state after animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 300);
  }, [clearTimeout]);

  const showToast = useCallback((message, options = {}) => {
    const {
      severity = 'info',
      duration = 3000,
      position = { vertical: 'top', horizontal: 'right' },
      action = null,
      preventDuplicate = false,
    } = options;

    // Check for duplicates if preventDuplicate is enabled
    if (preventDuplicate) {
      const duplicate = toastsRef.current.find(
        (t) => t.message === message && t.open && t.severity === severity
      );
      if (duplicate) {
        return duplicate.id;
      }
    }

    const id = generateId();

    setToasts((prev) => [
      ...prev,
      {
        id,
        message,
        severity,
        duration,
        position,
        action,
        open: true,
      },
    ]);

    // Auto-close after duration (if not null)
    if (duration !== null && duration > 0) {
      const timeoutId = setTimeout(() => {
        setToasts((prev) =>
          prev.map((toast) =>
            toast.id === id ? { ...toast, open: false } : toast
          )
        );
        
        setTimeout(() => {
          setToasts((prev) => prev.filter((toast) => toast.id !== id));
        }, 300);
      }, duration);

      timeoutsRef.current[id] = timeoutId;
    }

    return id;
  }, []);

  const success = useCallback(
    (message, options = {}) =>
      showToast(message, { ...options, severity: 'success' }),
    [showToast]
  );

  const error = useCallback(
    (message, options = {}) =>
      showToast(message, { ...options, severity: 'error' }),
    [showToast]
  );

  const warning = useCallback(
    (message, options = {}) =>
      showToast(message, { ...options, severity: 'warning' }),
    [showToast]
  );

  const info = useCallback(
    (message, options = {}) =>
      showToast(message, { ...options, severity: 'info' }),
    [showToast]
  );

  // Dismiss a specific toast by ID
  const dismiss = useCallback((id) => {
    handleClose(id);
  }, [handleClose]);

  // Clear all toasts
  const clearAll = useCallback(() => {
    // Clear all timeouts
    Object.keys(timeoutsRef.current).forEach((id) => {
      clearTimeout(timeoutsRef.current[id]);
    });
    timeoutsRef.current = {};
    
    // Clear all toasts
    setToasts([]);
  }, []);

  // Update an existing toast
  const update = useCallback((id, updates) => {
    setToasts((prev) =>
      prev.map((toast) =>
        toast.id === id ? { ...toast, ...updates } : toast
      )
    );
  }, []);

  // showToast/success/error/etc are all stable now, so this only ever
  // computes once - consumers never re-render just because ToastProvider did.
  const contextValue = useMemo(
    () => ({
      showToast,
      success,
      error,
      warning,
      info,
      dismiss,
      clearAll,
      update,
    }),
    [showToast, success, error, warning, info, dismiss, clearAll, update]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      {/* Render stacked snackbars */}
      {toasts.map((toast) => {
        const { position } = toast;

        // Find all toasts in the same position to calculate stacking
        const samePositionToasts = toasts.filter(
          (t) =>
            t.position.vertical === position.vertical &&
            t.position.horizontal === position.horizontal
        );
        const stackIndex = samePositionToasts.findIndex((t) => t.id === toast.id);

        return (
          <Snackbar
            key={toast.id}
            open={toast.open}
            autoHideDuration={toast.duration}
            onClose={() => handleClose(toast.id)}
            anchorOrigin={toast.position}
            TransitionComponent={SlideTransition}
            sx={{
              position: 'fixed',
              zIndex: 9999,
              // Space out multiple snackbars
              marginTop:
                toast.position.vertical === 'top' ? `${stackIndex * 70}px` : 0,
              marginBottom:
                toast.position.vertical === 'bottom' ? `${stackIndex * 70}px` : 0,
            }}
          >
            <Alert
              onClose={() => handleClose(toast.id)}
              severity={toast.severity}
              variant="filled"
              sx={{ width: '100%', '& .MuiAlert-message': { whiteSpace: 'pre-line' } }}
              action={
                toast.action || (
                  <IconButton
                    size="small"
                    aria-label="close"
                    color="inherit"
                    onClick={() => handleClose(toast.id)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }
            >
              {toast.message}
            </Alert>
          </Snackbar>
        );
      })}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

export default ToastProvider;